import React, { useEffect, useRef, useState } from 'react';
import { waitingClock, formatClock } from '../utils/waitingCharge';
import { hapticSelection } from '../utils/haptics';

/**
 * The live pickup waiting clock on the rider's trip screen.
 *
 * It appears ONLY when the server accepted the Anna's arrival as proof and the
 * rider accepted terms at booking (see utils/waitingCharge.js isClockRunning):
 * no clock means no charge, so the screen and the bill always agree. It never
 * shows during the quiet recording period or for rides on older apps.
 *
 * The amount is an estimate from this phone's clock — the server settles the real
 * charge when the PIN is accepted, and the receipt shows that.
 */
function WaitingClock({ ride, t }) {
  const [now, setNow] = useState(() => Date.now());
  const wasChargingRef = useRef(false);
  const running = Boolean(ride?.waitingPolicy && ride?.waitingStartedAt && ride?.status === 'arrived');

  // Tick once a second, only while there could be a clock to show.
  useEffect(() => {
    if (!running) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running, ride?.waitingStartedAt]);

  const clock = running ? waitingClock(ride, now) : null;

  // One gentle tap when the free time runs out and the meter starts.
  useEffect(() => {
    if (clock?.charging && !wasChargingRef.current) hapticSelection();
    wasChargingRef.current = Boolean(clock?.charging);
  }, [clock?.charging]);

  if (!clock) return null;
  const policy = ride.waitingPolicy;

  return (
    <div className="waiting-clock-card" role="status" aria-live="polite">
      <div className="waiting-clock-title">⏱ {t('active.waitingTitle')}</div>
      <div className={`waiting-clock-main${clock.charging ? ' charging' : ''}`}>
        {clock.charging
          ? t('active.waitingSoFar', { amount: clock.chargeSoFar, time: formatClock(clock.elapsedMs) })
          : t('active.waitingFreeLeft', { time: formatClock(clock.freeRemainingMs) })}
      </div>
      <div className="waiting-clock-sub">
        {t('active.waitingTerms', { free: policy.freeMinutes, rate: policy.perMinute })}
      </div>
      <div className="waiting-clock-sub">{t('active.waitingHint')}</div>
    </div>
  );
}

export default React.memo(WaitingClock, (prev, next) => (
  prev.t === next.t
  && prev.ride?.status === next.ride?.status
  && prev.ride?.driverId === next.ride?.driverId
  && prev.ride?.waitingDriverId === next.ride?.waitingDriverId
  && prev.ride?.waitingStartedAt === next.ride?.waitingStartedAt
  && prev.ride?.waitingPolicy === next.ride?.waitingPolicy
));
